import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import Cta from "@/components/Cta";
import Seo from "@/components/Seo";
import { Icon } from "@/components/Icon";
import { COMPANY } from "@/lib/content";

const details = [
  { icon: "Phone", label: "Phone", value: COMPANY.phone, href: COMPANY.phoneHref, testId: "contact-phone" },
  { icon: "Mail", label: "Email", value: COMPANY.email, href: COMPANY.emailHref, testId: "contact-email" },
  { icon: "MapPin", label: "Office", value: `${COMPANY.addressLine1}, ${COMPANY.addressLine2}`, href: COMPANY.mapLink, testId: "contact-address" },
  { icon: "CalendarClock", label: "Office Hours", value: COMPANY.hoursPlaceholder, testId: "contact-hours" },
];

const SOCIALS = [
  { icon: "Facebook", label: "Facebook", href: COMPANY.social.facebook, testId: "contact-social-facebook" },
  { icon: "Instagram", label: "Instagram", href: COMPANY.social.instagram, testId: "contact-social-instagram" },
  { icon: "Linkedin", label: "LinkedIn", href: COMPANY.social.linkedin, testId: "contact-social-linkedin" },
];

// Static contact form UI (front-end only). Wire to a backend/email service when needed.
const Field = ({ label, id, type = "text", placeholder }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="text-sm font-medium text-[#2A2E7A]">{label}</label>
    <input
      id={id}
      type={type}
      placeholder={placeholder}
      data-testid={`contact-field-${id}`}
      className="rounded-md border border-[#2A2E7A]/12 bg-white px-4 py-3 text-base text-[#2A2E7A] placeholder:text-[#9CA3AF] focus:border-[#4B5CAB] focus:outline-none focus:ring-2 focus:ring-[#4B5CAB]/20 transition-colors"
    />
  </div>
);

const Contact = () => {
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Contact Best Bizz Financial in Oranjestad, Aruba. Call +297 587 0606 or email office@bestbizz.biz to request a fixed monthly quote for bookkeeping, payroll or consultancy."
        path="/contact"
      />
      <PageHeader
        overline="Contact Us"
        title="Let's talk about your business"
        subtitle="Request a quote or ask a question — we'll get back to you with clear, straightforward answers."
      />

      <section className="bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Details */}
            <Reveal className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-semibold text-[#2A2E7A]">
                Get in touch
              </h2>
              <p className="mt-4 text-base text-[#51607A] leading-relaxed">
                Reach out by phone or email, and our team will be glad to help you
                with bookkeeping, payroll or consultancy.
              </p>
              <ul className="mt-8 space-y-6">
                {details.map((d) => (
                  <li key={d.label} className="flex items-start gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#F5F6FB]">
                      <Icon name={d.icon} className="h-6 w-6 text-[#4B5CAB]" />
                    </span>
                    <div>
                      <p className="text-sm font-medium uppercase tracking-wider text-[#9CA3AF]">
                        {d.label}
                      </p>
                      {d.href ? (
                        <a
                          href={d.href}
                          data-testid={d.testId}
                          target={d.testId === "contact-address" ? "_blank" : undefined}
                          rel={d.testId === "contact-address" ? "noopener noreferrer" : undefined}
                          className="mt-1 block text-lg font-medium text-[#2A2E7A] hover:text-[#4B5CAB] transition-colors"
                        >
                          {d.value}
                        </a>
                      ) : (
                        <p data-testid={d.testId} className="mt-1 text-lg font-medium text-[#2A2E7A]">
                          {d.value}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-8 border-t border-[#2A2E7A]/8">
                <p className="text-sm font-medium uppercase tracking-wider text-[#9CA3AF]">Follow Us</p>
                <div className="mt-4 flex items-center gap-3" data-testid="contact-socials">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      data-testid={s.testId}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[#2A2E7A]/15 text-[#2A2E7A] hover:bg-[#2A2E7A] hover:text-white hover:border-[#2A2E7A] transition-colors duration-200"
                    >
                      <Icon name={s.icon} className="h-5 w-5" strokeWidth={1.8} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Form (static UI) */}
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="rounded-lg border border-[#2A2E7A]/8 shadow-sm bg-white p-8 sm:p-10">
                <h2 className="font-heading text-2xl font-semibold text-[#2A2E7A]">
                  Request a Quote
                </h2>
                <p className="mt-2 text-base text-[#51607A]">
                  Tell us a little about your business and we'll prepare a fixed
                  monthly quote.
                </p>
                <form
                  className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6"
                  onSubmit={(e) => e.preventDefault()}
                  data-testid="contact-form"
                >
                  <Field label="Full Name" id="name" placeholder="Your name" />
                  <Field label="Company" id="company" placeholder="Company name" />
                  <Field label="Email" id="email" type="email" placeholder="you@company.com" />
                  <Field label="Phone" id="phone" type="tel" placeholder="+297 000 0000" />
                  <div className="sm:col-span-2 flex flex-col gap-2">
                    <label htmlFor="message" className="text-sm font-medium text-[#2A2E7A]">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      placeholder="How can we help your business?"
                      data-testid="contact-field-message"
                      className="rounded-md border border-[#2A2E7A]/12 bg-white px-4 py-3 text-base text-[#2A2E7A] placeholder:text-[#9CA3AF] focus:border-[#4B5CAB] focus:outline-none focus:ring-2 focus:ring-[#4B5CAB]/20 transition-colors resize-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Cta type="submit" variant="primary" className="w-full sm:w-auto" testId="contact-submit">
                      Send Request
                    </Cta>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>

          {/* Google Maps embed */}
          <Reveal delay={0.15} className="mt-16">
            <div className="overflow-hidden rounded-lg border border-[#2A2E7A]/8 shadow-sm">
              <iframe
                title="Best Bizz Financial office location — Sabana Liber 5B, Oranjestad, Aruba"
                data-testid="contact-map"
                src={COMPANY.mapEmbed}
                className="w-full h-72 sm:h-96 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
